/**
 * @file payments.api.js
 * @module PaymentsAPI
 * @description HTTP API wrapper for payment endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios";

/**
 * Payments API for handling payment-related operations.
 * @type {Object}
 * @property {Function} payReservations - Pay for reservations using a specified payment method.
 */
export const PaymentsAPI = {
    /**
     * Pay for reservations using a specified payment method.
     * @param {Object} payload - The payment payload containing reservation and payment details.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the payment response.
     */
    payReservations: (payload, token = '') => {
        return axiosInstance(token).post(`/payments/reservations`, payload)
    },

    /**
     * Get the payment aggregate of a single reservation (total, paid, pending,
     * refunded, status and method), re-synced against its current price.
     * @param {string} reservationId - The reservation id.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the reservation payment aggregate, including its `transactions`.
     */
    getReservationPayment: (reservationId, token = '') => {
        return axiosInstance(token).get(`/payments/reservations/${reservationId}`)
    },

    /**
     * Get payment summaries for a set of reservations.
     * @param {Object} payload - The request payload (e.g. reservation ids / filters) for the summary.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the payment summaries response.
     */
    getPaymentSummaries: (payload, token = '') => {
        return axiosInstance(token).post(`/payments/reservations/summary`, payload)
    },
    /**
     * Mark reservations as paid.
     * @param {Object} payload - The request payload containing reservation ids to mark as paid.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the response of marking reservations as paid.
     */
    markReservationsAsPaid: (payload, token = '') => {
        return axiosInstance(token).post(`/payments/reservations/mark-paid`, payload)
    }
}